import { useUserStore } from "$features/authentication/use-user-store";
import { HierarchyTreeNode } from "$features/search/hierarchy-tree/hierarchy-tree-node";
import { buildURL, cancellableFetch, useDebounce, useFrame } from "$shared/utils";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { HierarchyFacetViewModel } from "~/lib/data-contract";
import { SEARCH_FIELD_DEBOUNCE, SEARCH_URL } from "../service/endpoints";
import {fetcher} from "$utils/request/utils/fetcher";
import {Params} from "next/dist/shared/lib/router/utils/route-matcher";

const searchHierarchyQueryKey = 'useSearchHierarchy';
const searchHierarchyUrl = `${SEARCH_URL}/productsearch/getsearchhierarchy`;

function mapToTreeItem(item: HierarchyFacetViewModel, parentPath = ''): HierarchyTreeNode {
    const path = `${parentPath}/${item.displayName}`;
    return {
        children: (item.subHierarchyFacetViewModel ?? []).map(child => mapToTreeItem(child, path)),
        count: item.count || 0,
        id: item.key || undefined,
        label: item.displayName || '',
        path,
    }
}

export const useSearchHierarchy = (searchQuery: string) => {
    const { market } = useFrame();
    const customerNumber = useUserStore((state) => state.customerNumber);
    const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_FIELD_DEBOUNCE);

    const url = useMemo(() => buildURL(searchHierarchyUrl, {
        customerMarket: customerNumber || market?.anonymousCustomerMarketCode,
        searchText: debouncedSearchQuery,
    }), [customerNumber, debouncedSearchQuery]);

    const { data } = useQuery(
        [searchHierarchyQueryKey, debouncedSearchQuery],
        () => cancellableFetch<HierarchyFacetViewModel[]>({ url }), {
            enabled: Boolean(debouncedSearchQuery),
        }
    );

    return {
        rootNodes: (data || []).map(item => mapToTreeItem(item)),
    };
};

export const useSearchHierarchyFromFilters = ({
                                                  pageFilters,
                                                  userFilters,
                                              }: Params) => {
    const { market } = useFrame();
    const customerNumber = useUserStore((state) => state.customerNumber);
    const url = `${SEARCH_URL}/productsearch/getsearchhierarchyfromfilters`;

    const { data, isLoading, isError } = useQuery<HierarchyFacetViewModel[]>(
        ['searchHierarchyFromFilters', pageFilters, userFilters],
        () =>
            fetcher<HierarchyFacetViewModel[]>(url,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        customerMarket: customerNumber ?? market?.anonymousCustomerMarketCode,
                        pageFilters,
                        userFilters,
                    }),
                }
            )(),
        {
            enabled: pageFilters?.length > 0 || userFilters?.length > 0,
        }
    );

    return {
        rootNodes: (data || []).map(item => mapToTreeItem(item)),
        isLoading,
        isError,
    };
};
